import React from "react";
import ReactDOM from "react-dom/client";
import { Auth0Provider, useAuth0 } from "@auth0/auth0-react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import "./index.css";
import { router } from "./router";
import { ThemeProvider } from "./components/theme";
import { TenantProvider } from "./api/tenant-context";
import { Landing } from "./routes/marketing/Landing";
import { TermsPage, PrivacyPage } from "./routes/legal/LegalPages";

// Public legal pages are resolved ABOVE the auth gate so they render the same
// with or without a session and stay out of the authenticated router. Reached
// via full-page <a href> navigations (see LegalPages / Landing footers).
function publicLegalPage(): React.ReactNode | null {
  const path = window.location.pathname;
  if (path === "/terms" || path.startsWith("/terms/")) return <TermsPage />;
  if (path === "/privacy" || path.startsWith("/privacy/")) return <PrivacyPage />;
  return null;
}

const qc = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function Gate() {
  const { isLoading, isAuthenticated, error } = useAuth0();

  // Public legal pages render regardless of auth state.
  const legal = publicLegalPage();
  if (legal) return legal;

  if (isLoading) {
    return <FullScreen>Loading…</FullScreen>;
  }
  if (error) {
    return <FullScreen>Login error: {error.message}</FullScreen>;
  }
  // Unauthenticated visitors get the public marketing landing (with Log in /
  // Create account CTAs) instead of a bare button.
  if (!isAuthenticated) {
    return <Landing />;
  }
  return <RouterProvider router={router} />;
}

function FullScreen({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 text-slate-700 dark:bg-slate-950 dark:text-slate-300">
      {children}
    </div>
  );
}

const root = document.getElementById("root")!;
ReactDOM.createRoot(root).render(
  <React.StrictMode>
    <Auth0Provider
      domain={import.meta.env.VITE_AUTH0_DOMAIN}
      clientId={import.meta.env.VITE_AUTH0_CLIENT_ID}
      authorizationParams={{
        redirect_uri: window.location.origin,
        audience: import.meta.env.VITE_AUTH0_AUDIENCE,
        scope: "openid profile email",
      }}
      cacheLocation="localstorage"
    >
      <ThemeProvider>
        <QueryClientProvider client={qc}>
          <TenantProvider>
            <Gate />
          </TenantProvider>
        </QueryClientProvider>
      </ThemeProvider>
    </Auth0Provider>
  </React.StrictMode>,
);
